import {
  TextFieldRenderer,
  type TextFieldRendererProps,
  MaskTextFieldRenderer,
  type MaskTextFieldRendererProps,
  SearchSelectionRenderer,
  type SearchSelectionRendererProps,
  getMaskTextFieldRendererProps,
  getRendererProps,
} from '@exam-center/components';
import { ID_CARD_NUMBER_MASK, PHONE_NUMBER_MASK } from '@exam-center/utils';
import { Flex, SectionTitle, Text } from '@shared/components';

export interface ProctorPersonalInformationFieldsProps {
  fields: {
    idNumber: MaskTextFieldRendererProps;
    titleTh: SearchSelectionRendererProps;
    firstNameTh: TextFieldRendererProps;
    lastNameTh: TextFieldRendererProps;
    titleEn: SearchSelectionRendererProps;
    firstNameEn: TextFieldRendererProps;
    lastNameEn: TextFieldRendererProps;
    phoneNumber: MaskTextFieldRendererProps;
    email: TextFieldRendererProps;
    address: TextFieldRendererProps;
  };
}

export default function ProctorPersonalInformationFields(
  props: Readonly<ProctorPersonalInformationFieldsProps>,
) {
  const { fields } = props;

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
      <SectionTitle title='Personal Information' />
      <Flex sx={(theme) => ({ gap: theme.spacing(6) })}>
        <Text sx={{ flexBasis: '240px' }} variant='body1Semibold'>
          ข้อมูลส่วนตัว
        </Text>
        <Flex
          sx={{
            gap: (theme) => theme.spacing(4),
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <MaskTextFieldRenderer
            {...getMaskTextFieldRendererProps(
              fields.idNumber,
              { mask: ID_CARD_NUMBER_MASK },
              {
                sx: { maxWidth: '600px' },
                label: 'เลขบัตรประชาชน',
                placeholder: 'กรอกเลขบัตรประชาชน',
                required: true,
              },
            )}
          />
          <Flex sx={{ flex: 1, gap: (theme) => theme.spacing(4) }}>
            <SearchSelectionRenderer
              {...getRendererProps(fields.titleTh, {
                sx: { flexBasis: '260px' },
                label: 'คำนำหน้าชื่อ',
                placeholder: 'เลือกคำนำหน้า',
                required: true,
              })}
            />
            <TextFieldRenderer
              {...getRendererProps(fields.firstNameTh, {
                sx: { flex: 1 },
                label: 'ชื่อภาษาไทย',
                placeholder: 'กรอกชื่อภาษาไทย',
                required: true,
              })}
            />
            <TextFieldRenderer
              {...getRendererProps(fields.lastNameTh, {
                sx: { flex: 1 },
                label: 'นามสกุลภาษาไทย',
                placeholder: 'กรอกนามสกุลภาษาไทย',
                required: true,
              })}
            />
          </Flex>
          <Flex sx={{ flex: 1, gap: (theme) => theme.spacing(4) }}>
            <SearchSelectionRenderer
              {...getRendererProps(fields.titleEn, {
                sx: { flexBasis: '260px' },
                label: 'คำนำหน้าภาษาอังกฤษ',
                placeholder: 'เลือกคำนำหน้า',
                required: true,
              })}
            />
            <TextFieldRenderer
              {...getRendererProps(fields.firstNameEn, {
                sx: { flex: 1 },
                label: 'ชื่อภาษาอังกฤษ',
                placeholder: 'กรอกชื่อภาษาอังกฤษ',
                required: true,
              })}
            />
            <TextFieldRenderer
              {...getRendererProps(fields.lastNameEn, {
                sx: { flex: 1 },
                label: 'นามสกุลภาษาอังกฤษ',
                placeholder: 'กรอกนามสกุลภาษาอังกฤษ',
                required: true,
              })}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex sx={(theme) => ({ gap: theme.spacing(6) })}>
        <Text sx={{ flexBasis: '240px' }} variant='body1Semibold'>
          ข้อมูลสำหรับติดต่อ
        </Text>
        <Flex
          sx={{
            gap: (theme) => theme.spacing(4),
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <TextFieldRenderer
            {...getRendererProps(fields.email, {
              sx: { maxWidth: '600px' },
              label: 'อีเมลผู้ใช้ (อีเมลบริษัทเท่านั้น)',
              placeholder: 'กรอกอีเมลผู้ใช้',
              required: true,
            })}
          />
          <MaskTextFieldRenderer
            {...getMaskTextFieldRendererProps(
              fields.phoneNumber,
              { mask: PHONE_NUMBER_MASK },
              {
                sx: { maxWidth: '600px' },
                label: 'เบอร์โทรศัพท์',
                placeholder: 'กรอกเบอร์โทรศัพท์',
              },
            )}
          />
          <TextFieldRenderer
            {...getRendererProps(fields.address, {
              label: 'ที่อยู่สำหรับติดต่อ',
              placeholder: 'กรอกที่อยู่สำหรับติดต่อ',
            })}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}
